/**
 * Constantes transversales de la aplicación web. Cualquier número o cadena
 * con significado de negocio o de infraestructura vive aquí con su porqué,
 * nunca suelto en un componente.
 */

/** Milisegundos en un segundo; base para expresar intervalos legibles. */
const SECOND_MS = 1_000
/** Milisegundos en un minuto. */
const MINUTE_MS = 60 * SECOND_MS

/** Intervalos de refresco y tiempos de espera. */
export const INTERVALS = {
  /** Sondeo de la campana de notificaciones (1 minuto: suficiente y barato en datos móviles). */
  notificationsPollMs: MINUTE_MS,
  /** Tiempo durante el cual una consulta se considera fresca y no se vuelve a pedir. */
  queryStaleMs: 30 * SECOND_MS,
  /** El branding cambia muy poco: se mantiene fresco 10 minutos. */
  brandingStaleMs: 10 * MINUTE_MS,
  /** Tablero: se refresca cada 5 minutos mientras la pestaña está visible. */
  dashboardRefreshMs: 5 * MINUTE_MS,
  /** Espera tras la última tecla antes de buscar en el servidor. */
  searchDebounceMs: 300,
  /** Cajas abiertas de la agencia (vista del gerente): arqueo en vivo cada minuto. */
  openCashRefreshMs: MINUTE_MS,
  /** Duración de los avisos (toasts) de error, más largos para poder leer el código de referencia. */
  errorToastMs: 8 * SECOND_MS,
} as const

/** Reintentos de consultas cuando falla la red o el servidor responde 5xx. */
export const QUERY_MAX_RETRIES = 2

/** Tamaños de página y límites visuales. */
export const SIZES = {
  /** Filas por página por defecto en listados paginados. */
  defaultPageSize: 20,
  /** A partir de este número la insignia de notificaciones muestra "9+". */
  notificationsBadgeMax: 9,
  /** Filas esqueleto que se muestran mientras carga una tabla. */
  skeletonRows: 6,
} as const

/**
 * Claves de almacenamiento local. `theme` y `brandCss` también las lee
 * `public/theme-init.js` antes de pintar: si cambian aquí, cambiarlas allá.
 */
export const STORAGE_KEYS = {
  /** Token de refresco (localStorage si "recordar este dispositivo", si no sessionStorage). */
  refreshToken: 'tumin.refresh-token',
  /** Tema elegido: light | dark | system. */
  theme: 'tumin-theme',
  /** Densidad de la interfaz: comfortable | compact. */
  density: 'tumin.density',
  /** CSS de la paleta derivada del color de marca (evita el parpadeo al cargar). */
  brandCss: 'tumin-brand-css',
  /** Barra lateral colapsada en escritorio. */
  sidebarCollapsed: 'tumin.sidebar-collapsed',
} as const

/**
 * Estados HTTP que la app trata de forma especial (los demás se leen por
 * rango con `ApiError.isClientError`).
 */
export const HTTP_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  conflict: 409,
  requestTimeout: 408,
  locked: 423,
  tooManyRequests: 429,
} as const

/** Encabezados HTTP propios. */
export const HTTP_HEADERS = {
  /** Correlación de solicitudes con los registros del API. */
  requestId: 'X-Request-Id',
  /** Lo agrega el service worker cuando responde desde caché (valor = epoch ms del guardado). */
  cachedAt: 'X-Tumin-Cached-At',
} as const

/** Nombre del service worker publicado en `public/`. */
export const SERVICE_WORKER_URL = '/sw.js'

/** Mensajes que la app envía al service worker. */
export const SW_MESSAGES = {
  /** Borra las respuestas del API en caché (al cerrar o iniciar sesión). */
  clearApiCache: 'CLEAR_API_CACHE',
} as const

/** Localización de toda la interfaz (español de Guatemala). */
export const LOCALE = 'es-GT'
